import { api } from '../api/client.js';
import { createIcon } from '../components/icon.js';
import { createSkeleton } from '../components/skeleton.js';
import { getRouter } from '../router.js';
import { renderBody } from '../lib/body-renderer.js';

interface NewsDetailData {
  id: number;
  title: string;
  body: string;
  isPinned: boolean;
  publishedAt: string;
  authorName: string;
  tags?: string[];
  images: Array<{
    uuid: string;
    originalName: string | null;
    mimeType: string;
    width: number | null;
    height: number | null;
  }>;
  documents?: Array<{
    uuid: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
  }>;
}

function docIcon(mimeType: string): string {
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word') || mimeType.includes('opendocument.text')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType === 'application/zip') return '🗜️';
  return '📎';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export async function newsDetailPage(params: Record<string, string>): Promise<HTMLElement> {
  const router = getRouter();
  const page = document.createElement('div');
  page.className = 'page page--detail';
  page.setAttribute('data-title', 'Новость');

  const backBtn = document.createElement('button');
  backBtn.className = 'back-btn';
  backBtn.appendChild(createIcon('chevron-left', 20));
  const backSpan = document.createElement('span');
  backSpan.textContent = 'Назад';
  backBtn.appendChild(backSpan);
  backBtn.addEventListener('click', () => router.navigate('/'));
  page.appendChild(backBtn);

  const skeleton = createSkeleton('detail', 1);
  page.appendChild(skeleton);

  try {
    const news = await api.get<NewsDetailData>(`/news/${params.id}`);
    page.removeChild(skeleton);

    const title = document.createElement('h1');
    title.className = 'detail-title';
    title.textContent = news.title;
    page.appendChild(title);

    const meta = document.createElement('div');
    meta.className = 'detail-meta';
    const date = new Date(news.publishedAt).toLocaleDateString('ru-RU', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
    meta.innerHTML = `<time datetime="${news.publishedAt}">${date}</time> · ${news.authorName}`;
    page.appendChild(meta);

    // Tags — clickable, navigate to home with filter
    if (news.tags && news.tags.length > 0) {
      const tagsEl = document.createElement('div');
      tagsEl.className = 'detail-tags';
      for (const tag of news.tags) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'news-tag-chip news-tag-chip--clickable';
        chip.textContent = tag;
        chip.title = `Показать новости с тегом «${tag}»`;
        chip.addEventListener('click', () => {
          router.navigate(`/?tag=${encodeURIComponent(tag)}`);
        });
        tagsEl.appendChild(chip);
      }
      page.appendChild(tagsEl);
    }

    // Body with gallery support
    const body = document.createElement('div');
    body.className = 'detail-body';
    body.appendChild(renderBody(news.body, {
      onImageClick: (uuid, allUuids) => openLightbox(uuid, allUuids),
    }));
    page.appendChild(body);

    // Documents section
    if (news.documents && news.documents.length > 0) {
      const docsSection = document.createElement('div');
      docsSection.className = 'news-documents';

      const docsTitle = document.createElement('div');
      docsTitle.className = 'news-documents__title';
      docsTitle.textContent = 'Вложения';
      docsSection.appendChild(docsTitle);

      const docsList = document.createElement('div');
      docsList.className = 'news-documents__list';

      for (const doc of news.documents) {
        const item = document.createElement('a');
        item.className = 'news-document-item';
        item.href = `/uploads/docs/${doc.uuid}/${encodeURIComponent(doc.originalName)}`;
        item.target = '_blank';
        item.rel = 'noopener noreferrer';
        item.download = doc.originalName;

        const icon = document.createElement('span');
        icon.className = 'news-document-item__icon';
        icon.textContent = docIcon(doc.mimeType);
        item.appendChild(icon);

        const info = document.createElement('span');
        info.className = 'news-document-item__info';

        const name = document.createElement('span');
        name.className = 'news-document-item__name';
        name.textContent = doc.originalName;
        info.appendChild(name);

        const size = document.createElement('span');
        size.className = 'news-document-item__size';
        size.textContent = formatSize(doc.sizeBytes);
        info.appendChild(size);

        item.appendChild(info);

        const dl = document.createElement('span');
        dl.className = 'news-document-item__dl';
        dl.appendChild(createIcon('download', 16));
        item.appendChild(dl);

        docsList.appendChild(item);
      }

      docsSection.appendChild(docsList);
      page.appendChild(docsSection);
    }

  } catch (err) {
    page.removeChild(skeleton);
    const error = document.createElement('div');
    error.className = 'empty-state';
    error.innerHTML = '<p>Новость не найдена</p>';
    page.appendChild(error);
    console.error(err);
  }

  return page;
}

function openLightbox(currentUuid: string, allUuids: string[]): void {
  let currentIndex = allUuids.indexOf(currentUuid);
  if (currentIndex === -1) currentIndex = 0;

  const overlay = document.createElement('div');
  overlay.className = 'lightbox-overlay';

  const imgWrapper = document.createElement('div');
  imgWrapper.className = 'lightbox-image-wrapper';

  const img = document.createElement('img');
  img.className = 'lightbox-image';
  imgWrapper.appendChild(img);
  overlay.appendChild(imgWrapper);

  function showImage(index: number): void {
    const uuid = allUuids[index];
    if (!uuid) return;
    img.src = `/uploads/news/${uuid}/original.jpg`;
    img.alt = '';

    if (allUuids.length > 1) {
      const counter = overlay.querySelector('.lightbox-counter') as HTMLElement | null;
      if (counter) counter.textContent = `${index + 1} / ${allUuids.length}`;
    }
  }

  // Navigation — only show if multiple images
  if (allUuids.length > 1) {
    const prevBtn = document.createElement('button');
    prevBtn.className = 'lightbox-nav lightbox-nav--prev';
    prevBtn.type = 'button';
    prevBtn.setAttribute('aria-label', 'Предыдущее фото');
    prevBtn.appendChild(createIcon('chevron-left', 28));
    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      currentIndex = (currentIndex - 1 + allUuids.length) % allUuids.length;
      showImage(currentIndex);
    });
    overlay.appendChild(prevBtn);

    const nextBtn = document.createElement('button');
    nextBtn.className = 'lightbox-nav lightbox-nav--next';
    nextBtn.type = 'button';
    nextBtn.setAttribute('aria-label', 'Следующее фото');
    nextBtn.appendChild(createIcon('chevron-right', 28));
    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      currentIndex = (currentIndex + 1) % allUuids.length;
      showImage(currentIndex);
    });
    overlay.appendChild(nextBtn);

    const counter = document.createElement('div');
    counter.className = 'lightbox-counter';
    overlay.appendChild(counter);
  }

  const closeBtn = document.createElement('button');
  closeBtn.className = 'lightbox-close';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Закрыть');
  closeBtn.appendChild(createIcon('x', 24));
  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    close();
  });
  overlay.appendChild(closeBtn);

  function close(): void {
    document.body.removeChild(overlay);
    document.removeEventListener('keydown', onKeyDown);
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft' && allUuids.length > 1) {
      currentIndex = (currentIndex - 1 + allUuids.length) % allUuids.length;
      showImage(currentIndex);
    }
    if (e.key === 'ArrowRight' && allUuids.length > 1) {
      currentIndex = (currentIndex + 1) % allUuids.length;
      showImage(currentIndex);
    }
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target === imgWrapper) close();
  });

  document.addEventListener('keydown', onKeyDown);
  document.body.appendChild(overlay);
  showImage(currentIndex);
}
